import { Button } from '@/components/ui/shadcn/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Input } from '@/components/ui/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useTranslations } from '@/hooks/useTranslations';
import { ColumnVisibilityMenu } from '@/components/ui/table-cells/ColumnVisibilityMenu';
import { ArrowDownWideNarrow, Grid3x3, Search, SlidersHorizontal, SquareChartGantt, Table2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { FilterModal } from './FilterModal';
import type { ToolbarFilterOption } from './FilterPanel';
import { buildFilterDefinitions, type ExtraViewMode, type ToolbarViewMode } from './GenericToolbar';

interface GenericToolbarDesktopProps {
    quickFilterButtonLabel: string;
    quickFilterDefinitions: Array<{ id: string; label: string }>;
    columnDefinitions: Array<{ id: string; label: string }>;
    columnFilterOptions: Record<string, ToolbarFilterOption[]>;
    columnFilterLoading?: Record<string, boolean>;
    columnFilters: Record<string, string[]>;
    columnFilterSearch: Record<string, string>;
    setColumnFilterSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    toggleSingleFilter: (columnId: string, value: string) => void;
    handleFilterChange: (column: string, values: string[]) => void;
    clearFilters: () => void;
    isFilterOpen: boolean;
    setIsFilterOpen: (open: boolean) => void;
    isSortOpen: boolean;
    setIsSortOpen: (open: boolean) => void;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    handleSortChange: (column: string, direction: 'asc' | 'desc') => void;
    clearSort: () => void;
    search: string;
    setSearch: (value: string) => void;
    perPage?: number;
    onPerPageChange?: (perPage: number) => void;
    viewMode: ToolbarViewMode;
    setViewMode: (mode: ToolbarViewMode) => void;
    showGanttViewToggle?: boolean;
    showViewToggle?: boolean;
    extraViewMode?: ExtraViewMode;
    showPerPageSelect?: boolean;
    visibleColumns: Set<string>;
    handleColumnShow: (columnId: string) => void;
    handleColumnHide: (columnId: string) => void;
    filteredCount: number;
    resourceName: string;
    resourceNamePlural: string;
    sortableColumns?: Array<{ id: string; label: string }>;
    excludeFromMoreFilters?: string[];
    extraFilterDefinitions?: Array<{ id: string; label: string }>;
    customFilters?: ReactNode;
    /** Where customFilters sits relative to the checkbox groups. */
    customFiltersPosition?: 'start' | 'end';
    inlineFilters?: ReactNode;
    showMoreFilters?: boolean;
}

export function GenericToolbarDesktop({
    quickFilterButtonLabel,
    quickFilterDefinitions,
    columnDefinitions,
    columnFilterOptions,
    columnFilterLoading,
    columnFilters,
    columnFilterSearch,
    setColumnFilterSearch,
    toggleSingleFilter,
    handleFilterChange,
    clearFilters,
    isFilterOpen,
    setIsFilterOpen,
    isSortOpen,
    setIsSortOpen,
    sortColumn,
    sortDirection,
    handleSortChange,
    clearSort,
    search,
    setSearch,
    perPage,
    onPerPageChange,
    viewMode,
    setViewMode,
    visibleColumns,
    handleColumnShow,
    handleColumnHide,
    filteredCount,
    resourceName,
    resourceNamePlural,
    sortableColumns,
    excludeFromMoreFilters = [],
    extraFilterDefinitions = [],
    customFilters,
    customFiltersPosition = 'start',
    inlineFilters,
    showMoreFilters = true,
    showGanttViewToggle = false,
    showViewToggle = true,
    extraViewMode,
    showPerPageSelect = true,
}: GenericToolbarDesktopProps) {
    const { t } = useTranslations();
    const translationKey = `${resourceName}.index`;
    const activeFilterCount = Object.values(columnFilters).reduce((count, values) => count + values.length, 0);
    const hasActiveFilters = activeFilterCount > 0;
    const commandButtonClass =
        'h-9 rounded-md border-border bg-background px-3 text-foreground hover:bg-accent hover:text-accent-foreground';

    // Everything filterable, in one modal: quick filters first, then the remaining columns.
    const modalFilterDefinitions = buildFilterDefinitions({
        quickFilterDefinitions,
        columnDefinitions,
        extraFilterDefinitions,
        excludeFromMoreFilters,
        showMoreFilters,
    });

    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <FilterModal
                    open={isFilterOpen}
                    onOpenChange={setIsFilterOpen}
                    title={t('common.filters', 'Filters')}
                    resourceNamePlural={resourceNamePlural}
                    filteredCount={filteredCount}
                    filterDefinitions={modalFilterDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilterLoading={columnFilterLoading}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    header={customFiltersPosition === 'start' ? customFilters : undefined}
                    footer={customFiltersPosition === 'end' ? customFilters : undefined}
                    trigger={
                        <Button
                            variant={hasActiveFilters ? 'default' : 'outline'}
                            size="sm"
                            title={quickFilterButtonLabel}
                            className={`h-9 gap-2 rounded-md px-3 ${hasActiveFilters ? '' : 'border-primary/40 text-primary hover:bg-primary/10 hover:text-primary'}`}
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            {hasActiveFilters ? t('common.filtered', 'Filtered') : t('common.filters', 'Filters')}
                            {hasActiveFilters && (
                                <span className="ml-1 rounded-sm bg-primary-foreground/20 px-1.5 py-0.5 text-[11px] leading-none">
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                    }
                />

                {inlineFilters}

                {sortableColumns && sortableColumns.length > 0 && (
                    <DropdownMenu open={isSortOpen} onOpenChange={setIsSortOpen}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className={`gap-2 ${commandButtonClass}`}>
                                <ArrowDownWideNarrow className="h-4 w-4" />
                                {/*{t(`${translationKey}.toolbar.sort`, 'Sort')}*/}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                            <DropdownMenuLabel>{t(`${translationKey}.toolbar.sort_by`, 'Sort by')}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {sortableColumns.map((item) => (
                                <div key={item.id} className="flex items-center justify-between px-2 py-1">
                                    <span className="text-sm">{item.label}</span>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={`h-7 w-7 ${sortColumn === item.id && sortDirection === 'asc' ? 'bg-muted' : ''}`}
                                            onClick={() => handleSortChange(item.id, 'asc')}
                                        >
                                            ↑
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={`h-7 w-7 ${sortColumn === item.id && sortDirection === 'desc' ? 'bg-muted' : ''}`}
                                            onClick={() => handleSortChange(item.id, 'desc')}
                                        >
                                            ↓
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={clearSort}>{t(`${translationKey}.toolbar.clear_sort`, 'Clear sort')}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}

                <div className="relative">
                    <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="h-9 w-[280px] rounded-md border-border bg-background pl-9"
                        placeholder={t(`${translationKey}.toolbar.search_placeholder`, 'Search...')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
                {showPerPageSelect && perPage !== undefined && onPerPageChange ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {t(`${translationKey}.toolbar.per_page`, 'Per page')}:
                        <Select value={String(perPage)} onValueChange={(value) => onPerPageChange(Number(value))}>
                            <SelectTrigger className="h-9 w-24 rounded-md border-border bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[10, 25, 50, 100].map((option) => (
                                    <SelectItem key={option} value={String(option)}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}
                <p className="text-sm text-muted-foreground">
                    {filteredCount} {resourceNamePlural} {t(`${translationKey}.toolbar.found`, 'found')}
                </p>
                {showViewToggle && (
                    <div className="flex items-center gap-0.5 rounded-md bg-muted p-1">
                        <Button
                            variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 w-8 rounded-sm px-0"
                            onClick={() => setViewMode('table')}
                            aria-label={t(`${translationKey}.toolbar.view_table`, 'Table')}
                        >
                            <Table2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 w-8 rounded-sm px-0"
                            onClick={() => setViewMode('grid')}
                            aria-label={t(`${translationKey}.toolbar.view_grid`, 'Grid')}
                        >
                            <Grid3x3 className="h-4 w-4" />
                        </Button>
                        {showGanttViewToggle ? (
                            <Button
                                variant={viewMode === 'gantt' ? 'secondary' : 'ghost'}
                                size="sm"
                                className="h-8 w-8 rounded-sm px-0"
                                onClick={() => setViewMode('gantt')}
                                title={t(`${translationKey}.toolbar.view_gantt`, 'Gantt')}
                                aria-label={t(`${translationKey}.toolbar.view_gantt`, 'Gantt')}
                            >
                                <SquareChartGantt className="h-4 w-4" />
                            </Button>
                        ) : null}
                        {extraViewMode ? (
                            <Button
                                variant={viewMode === extraViewMode.value ? 'secondary' : 'ghost'}
                                size="sm"
                                className="h-8 w-8 rounded-sm px-0"
                                onClick={() => setViewMode(extraViewMode.value)}
                                title={extraViewMode.label}
                                aria-label={extraViewMode.label}
                            >
                                {extraViewMode.icon}
                            </Button>
                        ) : null}
                    </div>
                )}
                {viewMode === 'table' && (
                    <ColumnVisibilityMenu
                        allColumns={columnDefinitions}
                        visibleColumns={visibleColumns}
                        onColumnShow={handleColumnShow}
                        onColumnHide={handleColumnHide}
                    />
                )}
            </div>
        </div>
    );
}
